import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, KeyRound, Mail, Unlink } from "lucide-react";
import type { LoginMoveProvider, PlayerIdentityRow, UnlinkProvider } from "@/lib/api";
import { UnlinkIdentityDialog } from "@/components/player-actions/UnlinkIdentityDialog";
import { ResetPasswordDialog } from "@/components/player-actions/ResetPasswordDialog";
import { MigrationEmailDialog } from "@/components/player-actions/MigrationEmailDialog";
import { MoveLoginDialog } from "@/components/player-actions/MoveLoginDialog";
import { ProviderIcon } from "./ProviderIcon";
import { providerLabel } from "./player-presenters";

const UNLINKABLE = new Set<string>(["google", "discord", "apple", "password"]);
// Each provider has a reviewed server move (move-<provider>-login).
const SOCIAL: readonly LoginMoveProvider[] = ["google", "discord", "apple"];
type Action = { kind: "unlink"; provider: UnlinkProvider } | { kind: "reset" } | { kind: "email" } | { kind: "move"; provider: LoginMoveProvider } | null;

/** Keep email and social login options visible, including providers not linked yet. */
export function LinkedIdentities({ id, displayName, identities, readOnly, busy }: {
  id: string; displayName: string; identities: PlayerIdentityRow[]; readOnly: boolean; busy: boolean;
}) {
  const qc = useQueryClient();
  const [action, setAction] = useState<Action>(null);
  const [notice, setNotice] = useState("");
  const target = { id, displayName };
  const linked = new Set(identities.map(identity => identity.provider));
  // Mirrors the server rule: another social login or an email/password login must remain.
  const signInMethods = identities.filter(identity => UNLINKABLE.has(identity.provider)).length;
  const blocked = readOnly ? "Read-only preview" : busy ? "Finish saving first" : undefined;
  const close = () => { setAction(null); void qc.invalidateQueries({ queryKey: ["player-identities", id] }); };

  return <>
    {notice && <p className="pw-notice" role="status">{notice}</p>}
    {!linked.has("password") && <div className="pw-identity is-unlinked">
      <span className="pw-identity-icon"><ProviderIcon provider="password" size={17} /></span>
      <div><strong>{providerLabel("password")}</strong><span>Not linked</span></div>
      <div className="pw-identity-actions">
        <button className="pw-button" disabled={!!blocked} title={blocked}
          onClick={() => setAction({ kind: "move", provider: "password" })}><ArrowRightLeft size={13} />Move from another account</button>
        <button className="pw-button" disabled={!!blocked} title={blocked}
          onClick={() => setAction({ kind: "email" })}><Mail size={13} />Change linked email</button>
      </div>
    </div>}
    {[...identities].sort((a, b) => Number(b.provider === "password") - Number(a.provider === "password")).map((identity, index) => {
      const unlinkable = UNLINKABLE.has(identity.provider);
      const lastMethod = unlinkable && signInMethods <= 1;
      return <div className="pw-identity" key={`${identity.provider}-${index}`}>
        <span className="pw-identity-icon"><ProviderIcon provider={identity.provider} size={17} /></span>
        <div><strong>{providerLabel(identity.provider)}</strong><span>{identity.email ?? identity.display_name ?? identity.subject}</span></div>
        <div className="pw-identity-actions">
          {identity.provider === "password" && <button className="pw-button" disabled={!!blocked} title={blocked}
            onClick={() => setAction({ kind: "email" })}><Mail size={13} />Change linked email</button>}
          {identity.provider === "password" && <button className="pw-button" disabled={!!blocked} title={blocked}
            onClick={() => setAction({ kind: "reset" })}><KeyRound size={13} />Reset password</button>}
          {unlinkable ? <button className="pw-button pw-button-danger" disabled={!!blocked || lastMethod}
            title={blocked ?? (lastMethod ? "This is the only sign-in method left" : undefined)}
            onClick={() => setAction({ kind: "unlink", provider: identity.provider as UnlinkProvider })}><Unlink size={13} />Unlink</button>
            : <span className="pw-caption">Linked</span>}
        </div>
      </div>;
    })}
    {SOCIAL.filter(provider => !linked.has(provider)).map(provider =>
      <div className="pw-identity is-unlinked" key={provider}>
        <span className="pw-identity-icon"><ProviderIcon provider={provider} size={17} /></span>
        <div><strong>{providerLabel(provider)}</strong><span>Not linked</span></div>
        <div className="pw-identity-actions">
          <button className="pw-button" disabled={!!blocked} title={blocked}
            onClick={() => setAction({ kind: "move", provider })}><ArrowRightLeft size={13} />Move from another account</button>
        </div>
      </div>)}
    {action?.kind === "unlink" && <UnlinkIdentityDialog target={target} provider={action.provider} onClose={close} />}
    {action?.kind === "email" && <MigrationEmailDialog target={target} onClose={close}
      initialEmail={identities.find(identity => identity.provider === "password")?.email ?? identities.find(identity => identity.provider === "password")?.subject ?? ""} />}
    {action?.kind === "reset" && <ResetPasswordDialog target={target} onClose={close} />}
    {action?.kind === "move" && <MoveLoginDialog provider={action.provider} target={target} into onClose={close}
      onMoved={message => { setNotice(message); close(); }} />}
  </>;
}
